import { createTranslationHook } from './i18nFactory';
import type { TranslateFn } from './i18nFactory';

/**
 * i18n del core de MasterDocument.
 *
 * Cubre el "chrome" que el kit renderiza por su cuenta: `aria-label`, `title`,
 * `placeholder`, textos de estado vacío, carga y paginación. NO cubre el texto
 * que es dato del host (`FieldDescriptor.label`, `action.label`,
 * `ValidationRule.message`, `identity.breadcrumbLabel`): ese pertenece al
 * template y el kit no debe traducirlo.
 *
 * Usa `useTranslation` de siesa-ui-kit vía `createTranslationHook`; si el host no
 * registró las claves `master_document.*` (el motor devuelve la propia clave),
 * cae al diccionario local del idioma activo y, en último término, al español.
 * Mismo contrato que `WithholdingsPanel/i18n.ts`, `SerialPanel/i18n.ts`, etc.
 *
 * Convención de claves: planas con puntos (`DefaultTranslationEngine` indexa
 * `resources[key]` directo), prefijo `master_document.` en snake_case por ser
 * multipalabra, grupo y hoja en camelCase.
 *
 * ## Namespaces reservados por el feature
 *
 * El catálogo del backend es GLOBAL: todas las claves de todos los features viven
 * en el mismo espacio de nombres, así que estos prefijos están reservados y no
 * deben reutilizarse en otro módulo.
 *
 * | Prefijo                  | Dueño                      |
 * |--------------------------|----------------------------|
 * | `master_document.*`      | core de MasterDocument     |
 * | `serial_panel.*`         | SerialPanel                |
 * | `withholdings.*`         | WithholdingsPanel          |
 * | `installments.*`         | InstallmentsPanel          |
 * | `advances.*`             | AdvanceApplicationsPanel   |
 * | `financial_instrument.*` | FinancialInstrumentPanel   |
 *
 * Deuda conocida: los cinco sub-paneles no llevan prefijo de feature, y
 * `advances.*` / `installments.*` son lo bastante genéricos para colisionar con
 * otro módulo. NO se renombraron a `master_document.<panel>.*` a propósito: esas
 * claves pueden estar ya servidas por el backend y el renombrado dejaría sin
 * traducción a los hosts que las tengan registradas. Es un cambio que hay que
 * coordinar con backend, no un refactor unilateral.
 *
 * @see src/components/MasterDocument/i18nFactory.ts - Resolución y fallback
 * @see src/components/MasterDocument/commonTexts.ts - Textos genéricos compartidos
 */

export const MASTER_DOCUMENT_TRANSLATIONS_ES: Record<string, string> = {
  // ─── Chrome de modales / diálogos ───
  'master_document.chrome.close': 'Cerrar',
  'master_document.chrome.closeDialog': 'Cerrar diálogo',
  'master_document.chrome.closeNotice': 'Cerrar aviso',
  'master_document.chrome.restoreSize': 'Restaurar tamaño',
  'master_document.chrome.maximize': 'Maximizar',
  'master_document.chrome.updating': 'Actualizando',
  'master_document.chrome.updatingTitle': 'Actualizando…',
  // Campo solo-lectura resolviendo la etiqueta de su lookup (LookupReadonlyValue).
  'master_document.chrome.loadingValue': 'Cargando valor',
  'master_document.chrome.actionRunning': 'Ejecutando acción',
  'master_document.chrome.confirm': 'Confirmar',
  'master_document.chrome.cancel': 'Cancelar',
  'master_document.chrome.back': 'Atrás',
  // Salida de la pantalla de detalle. NO se reutiliza `chrome.back` ('Atrás'),
  // que es el paso anterior de un asistente: son dos ideas distintas.
  'master_document.chrome.backToList': 'Volver',
  'master_document.chrome.next': 'Siguiente',
  'master_document.chrome.error': 'Error',

  // ─── Barra de acciones / encabezado ───
  'master_document.actionBar.aria': 'Barra de acciones del documento',
  'master_document.actionBar.moreActions': 'Más acciones',
  'master_document.actionBar.transitions': 'Transiciones',
  'master_document.actionBar.actions': 'Acciones',
  'master_document.header.sectionsAria': 'Secciones del encabezado',
  'master_document.contextStrip.aria': 'Contexto del documento',

  // ─── FactBox ───
  'master_document.factBox.aria': 'Panel de información',

  // ─── FactBox: condiciones de pago ───
  'master_document.factBox.paymentTerms.dueDays': 'Días vencimiento',
  'master_document.factBox.paymentTerms.earlyPaymentEnabled': 'Pronto pago habilitado',
  'master_document.factBox.paymentTerms.window': 'Ventana',
  'master_document.factBox.paymentTerms.advanceRequired': 'Anticipo requerido',
  'master_document.factBox.paymentTerms.advanceAmount': 'Monto anticipo',
  'master_document.factBox.paymentTerms.days_one': '{count} día',
  'master_document.factBox.paymentTerms.days_other': '{count} días',
  'master_document.factBox.paymentTerms.installments': 'Cuotas',
  'master_document.factBox.paymentTerms.mode': 'Modo',
  'master_document.factBox.paymentTerms.discount': 'Descuento',
  'master_document.factBox.paymentTerms.periodDays': 'Dias',
  'master_document.factBox.paymentTerms.periodMonths': 'Meses',
  'master_document.factBox.paymentTerms.periodCustom': 'Personalizado',

  // ─── FactBox: financiero ───
  'master_document.factBox.financial.receivables': 'Cuentas por cobrar',
  'master_document.factBox.financial.payables': 'Cuentas por pagar',
  'master_document.factBox.financial.crossedAdvances': 'Anticipos cruzados',
  'master_document.factBox.financial.localReceivables': 'CxC local',
  'master_document.factBox.financial.localPayables': 'CxP local',
  'master_document.factBox.financial.cash': 'Efectivo',

  // ─── FactBox CFDE / factura electrónica ───
  'master_document.cfde.dianStatus': 'Estado DIAN',
  'master_document.cfde.feNumber': 'Número FE',
  'master_document.cfde.issueDate': 'Fecha emisión',
  'master_document.cfde.prints': 'Impresiones',
  'master_document.cfde.retransmit': 'Retransmitir documento a la DIAN',
  'master_document.cfde.downloadPdf': 'Descargar PDF de la factura electrónica',
  'master_document.cfde.status.pending': 'Pendiente',
  'master_document.cfde.status.sent': 'Enviada',
  'master_document.cfde.status.accepted': 'Aceptada',
  'master_document.cfde.status.rejected': 'Rechazada',
  'master_document.cfde.status.error': 'Error',
  'master_document.cfde.statusAria': 'Estado: {status}',

  // ─── Historial de estados: etiquetas de transición del backend ───
  'master_document.statusHistory.action.create': 'Crear documento',
  'master_document.statusHistory.action.save': 'Guardar',
  'master_document.statusHistory.action.approve': 'Aprobar',
  'master_document.statusHistory.action.reject': 'Rechazar',
  'master_document.statusHistory.action.void': 'Anular',
  'master_document.statusHistory.action.print': 'Imprimir',
  'master_document.statusHistory.action.sendToApproval': 'Enviar a aprobación',
  'master_document.statusHistory.action.hold': 'Retener',
  'master_document.statusHistory.action.release': 'Liberar',
  'master_document.statusHistory.action.commit': 'Comprometer',
  'master_document.statusHistory.action.dispatch': 'Despachar',
  'master_document.statusHistory.action.post': 'Contabilizar',
  'master_document.statusHistory.action.invoice': 'Facturar',
  'master_document.statusHistory.action.fulfill': 'Cumplir',
  'master_document.statusHistory.action.partialFulfillment': 'Cumplimiento parcial',
  'master_document.statusHistory.action.advance': 'Anticipo',
  'master_document.statusHistory.action.collection': 'Recaudo',

  // ─── Banner de aprobación ───
  'master_document.approval.approve': 'Aprobar',
  'master_document.approval.reject': 'Rechazar',

  // ─── Badge de cumplimiento de línea ───
  'master_document.lineFulfillment.pending': 'Pendiente',
  'master_document.lineFulfillment.partial': 'Parcial',
  'master_document.lineFulfillment.fulfilled': 'Cumplida',
  'master_document.lineFulfillment.closed': 'Cerrada',

  // ─── Listado de documentos ───
  'master_document.list.rowActions': 'Acciones',
  'master_document.list.rowOptions': 'Opciones',
  'master_document.list.selectAll': 'Seleccionar todos',
  'master_document.list.selectRow': 'Seleccionar fila',
  'master_document.list.previousPage': 'Página anterior',
  'master_document.list.nextPage': 'Página siguiente',
  'master_document.list.reload': 'Recargar',
  'master_document.list.selectCompany': 'Seleccionar compañía',
  'master_document.list.noneSelected': 'Ningún documento seleccionado',
  'master_document.list.noRecords': 'No hay registros',
  'master_document.list.loadError': 'Error al cargar los datos',
  'master_document.list.new': 'Nuevo',
  // Barra del listado y paginador, alineados al canon de MasterCrud.
  'master_document.list.moreActions': 'Más acciones',
  // Acción de ESTADO del documento. El rótulo es del paquete, como el
  // `ui-mastercrud.deactivateRecord` de MasterCrud, para que todos los documentos
  // digan lo mismo; el catálogo del host puede sobrescribirlo.
  'master_document.list.viewDetail': 'Ver detalle',
  'master_document.list.deactivateRecord': 'Desactivar registro',
  'master_document.list.activateRecord': 'Activar registro',
  'master_document.list.moreOptions': 'Más opciones',
  'master_document.list.hideAdvancedSearch': 'Ocultar búsqueda avanzada',
  'master_document.list.perPage': 'por página',
  'master_document.list.records': 'registros',
  'master_document.list.goToPage': 'Página',
  'master_document.list.noResults': 'No se encontraron documentos',
  'master_document.list.export': 'Exportar',
  'master_document.list.print': 'Imprimir',
  'master_document.list.confirmAction': '¿Desea ejecutar esta acción?',
  'master_document.list.allCompanies': 'Global — Todas las compañías',
  'master_document.list.action.delete': 'Eliminar',
  'master_document.list.action.void': 'Anular',

  // ─── Sección de líneas ───
  'master_document.lines.selectLine': 'Seleccionar línea',
  'master_document.lines.selectAllLines': 'Seleccionar todas las líneas',
  'master_document.lines.editLine': 'Editar línea',
  'master_document.lines.view': 'Ver',
  'master_document.lines.viewLine': 'Ver línea',
  'master_document.lines.moreOptions': 'Más opciones',
  'master_document.lines.moreLineOptions': 'Más opciones de línea',
  'master_document.lines.closeLine': 'Cerrar línea',
  'master_document.lines.reopenLine': 'Reabrir línea',
  // Mensaje de obligatorio cuando el descriptor trae solo el flag `required` y no una
  // regla `{ type: 'required' }` con su propio texto. En el módulo comercial son 5 campos.
  'master_document.lines.fieldRequired': 'Este campo es obligatorio.',
  'master_document.lines.emptyTitle': 'No hay líneas de detalle',
  'master_document.lines.emptyHint': 'Agrega una nueva línea para comenzar',
  'master_document.lines.closeReasonPlaceholder': 'Describe el motivo del cierre...',
  'master_document.lines.title': 'Líneas del documento',
  'master_document.lines.newLine': 'Nueva línea',
  'master_document.lines.addLine': 'Agregar línea',
  'master_document.lines.saveChanges': 'Guardar cambios',
  'master_document.lines.edit': 'Editar',
  'master_document.lines.duplicate': 'Duplicar',
  'master_document.lines.delete': 'Eliminar',
  'master_document.lines.deleteConfirm': '¿Eliminar esta línea? Esta acción no se puede deshacer.',

  // ─── Documento ───
  'master_document.document.deleteConfirm':
    '¿Eliminar este documento? Esta acción no se puede deshacer.',
  'master_document.document.traceability': 'Trazabilidad',
  'master_document.document.auditTitle': 'Historial de cambios',
  'master_document.document.print': 'Imprimir',
  'master_document.document.printNotImplemented':
    'La función de impresión de este documento aún no está implementada.',
  'master_document.document.conflictBody':
    'Otra sesión modificó este documento. Tus cambios no se guardaron. Recarga para ver la versión actual y vuelve a aplicarlos.',

  // ─── Campos / búsqueda ───
  'master_document.field.search': 'Buscar',
  'master_document.field.searchPlaceholder': 'Buscar...',
  'master_document.field.selectPlaceholder': 'Seleccionar...',
  'master_document.field.required': 'Este campo es obligatorio',
  'master_document.field.lockTooltip': 'Elimine las líneas para poder cambiar este campo.',

  // ─── Asistente de creación ───
  'master_document.wizard.aria': 'Nuevo documento',
  'master_document.wizard.selectCompany': 'Seleccionar compañía...',
  'master_document.wizard.selectDocumentClass': 'Seleccionar clase de documento...',
  'master_document.wizard.selectOperationCenter': 'Seleccionar centro de operación...',

  // ─── Moneda ───
  'master_document.currency.searchPlaceholder': 'Buscar moneda...',
  'master_document.currency.listAria': 'Monedas disponibles',

  // ─── Descuento global ───
  'master_document.discount.applyPercent': 'Aplicar descuento en porcentaje',
  'master_document.discount.applyAmount': 'Aplicar descuento en monto fijo',
  'master_document.discount.valueAria': 'Valor de descuento',

  // ─── Cruce de documentos ───
  'master_document.crossing.searchPlaceholder': 'Buscar por número de documento...',
  'master_document.crossing.addPlaceholder': 'N° de documento y Enter para agregar...',
  'master_document.crossing.selectRow': 'Seleccionar',
  'master_document.crossing.bulkProgress': 'Procesando documentos...',
  'master_document.crossing.bulkCompleted': 'Completado exitosamente',
  'master_document.crossing.traceabilityLoading': 'Cargando árbol de trazabilidad',
  'master_document.crossing.pending.item': 'Ítem',
  'master_document.crossing.pending.document': 'Documento',
  'master_document.crossing.pending.pending': 'Pendiente',
  'master_document.crossing.pending.state': 'Estado',
  'master_document.crossing.pending.quantityToCross': 'Cantidad a cruzar',
  'master_document.crossing.selectLineToGenerate':
    'Selecciona al menos una línea para generar el documento.',
  'master_document.crossing.noGeneratedDocument': 'El backend no devolvió el documento generado.',
  'master_document.crossing.pending.selectLineAria': 'Seleccionar línea {item}',
  'master_document.crossing.pending.selectDocumentAria': 'Seleccionar {document}',
  'master_document.crossing.pending.quantityAria': 'Cantidad a cruzar para {item}',

  // ─── Panel de cruce: textos con canal de override por template ───
  // El default deja de ser un literal y pasa por `t()`, para que el kit respete
  // el idioma activo cuando el host no sobreescribe el texto.
  'master_document.crossing.panelTitle': 'Cruzar documentos origen',
  'master_document.crossing.emptyAvailable': 'No hay documentos origen disponibles para cruzar.',
  'master_document.crossing.emptyCrossed': 'Sin documentos origen cruzados.',
  'master_document.crossing.subtotalLabel': 'Subtotal de cruce',
  'master_document.crossing.crossButton': 'Cruzar seleccionados',
  'master_document.crossing.uncross': 'Desvincular',
  'master_document.crossing.loading': 'Cargando…',
  'master_document.crossing.crossing': 'Cruzando…',
  'master_document.crossing.blockedLinesCount_one': '{count} línea no cruzada',
  'master_document.crossing.blockedLinesCount_other': '{count} líneas no cruzadas',
  'master_document.crossing.lineCount_one': '{count} línea',
  'master_document.crossing.lineCount_other': '{count} líneas',
  'master_document.crossing.terminalLineMessage':
    'No se puede seleccionar una línea cerrada o cumplida',
  'master_document.crossing.quantityRequired': 'Ingresa una cantidad a cruzar.',
  'master_document.crossing.quantityPositive': 'La cantidad debe ser mayor que cero.',
  'master_document.crossing.quantityExceedsPending':
    'La cantidad no puede superar lo pendiente ({pending}{uom}).',

  // ─── Asistente "crear desde origen" (canal `config.i18n`) ───
  'master_document.createFromSource.originSelectorTitle': 'Nuevo documento',
  'master_document.createFromSource.step1Title': 'Seleccionar documentos origen',
  'master_document.createFromSource.step2Title': 'Seleccionar líneas',
  'master_document.createFromSource.fromSourceOption': 'Generar desde origen',
  'master_document.createFromSource.freeCaptureOption': 'Captura directa (sin origen)',
  'master_document.createFromSource.loadingSources': 'Cargando documentos origen…',
  'master_document.createFromSource.emptyState': 'No hay documentos origen disponibles.',
  'master_document.createFromSource.singleSelectHint':
    'Solo se puede seleccionar un documento origen.',
  'master_document.createFromSource.loadingLines': 'Cargando líneas pendientes…',
  'master_document.createFromSource.emptyLines_one':
    'No hay líneas pendientes en el documento seleccionado.',
  'master_document.createFromSource.emptyLines_other':
    'No hay líneas pendientes en los documentos seleccionados.',
  'master_document.createFromSource.quantityColumn': 'Cantidad a cruzar',
  'master_document.createFromSource.confirmSummary':
    'Se crearán {lines} {linesLabel} desde {sources} {sourcesLabel}.',
  'master_document.createFromSource.lineNoun_one': 'línea',
  'master_document.createFromSource.lineNoun_other': 'líneas',
  'master_document.createFromSource.sourceNoun_one': 'documento origen',
  'master_document.createFromSource.sourceNoun_other': 'documentos origen',
  'master_document.createFromSource.incompatibleReason':
    'Incompatible con el primer origen ({keys}).',
  'master_document.createFromSource.columns.document': 'Documento',
  'master_document.createFromSource.columns.thirdParty': 'Tercero',
  'master_document.createFromSource.columns.date': 'Fecha',
  'master_document.createFromSource.columns.state': 'Estado',
  'master_document.createFromSource.columns.total': 'Total',

  // ─── Estados de carga ───
  'master_document.loading.document': 'Cargando documento...',
  'master_document.loading.list': 'Cargando lista...',
  'master_document.staleData.refresh': 'Actualizar datos',

  // ─── Errores genéricos ───
  // Los usan módulos puros (`smart/lineActionGating`, `smart/crossingFetch`) que no
  // pueden llamar a un hook: propagan la clave y el hook llamante la resuelve.
  'master_document.error.actionFailed': 'No se pudo completar la acción.',
  'master_document.error.unexpected': 'Ocurrió un error inesperado. Intenta de nuevo.',
  'master_document.error.autoSaveTotals': 'Auto-save totales: {detail}',
  // Clasificación de errores para el usuario (ver smart/userFacingError.ts). El
  // texto del backend, cuando existe, tiene prioridad sobre estas claves.
  'master_document.error.unauthorized': 'Tu sesión expiró. Vuelve a iniciar sesión.',
  'master_document.error.forbidden': 'No tienes permiso para esta operación.',
  'master_document.error.notFound': 'El documento no existe o fue eliminado.',
  'master_document.error.conflict':
    'El documento cambió desde que lo abriste. Recarga para ver la versión actual.',
  'master_document.error.validation': 'Revisa los datos ingresados.',
  'master_document.error.serverUnavailable':
    'El servicio no está disponible. Intenta de nuevo en unos minutos.',
  'master_document.error.network':
    'No hay conexión con el servidor. Revisa tu red e intenta de nuevo.',

  // ═══════════════════════════════════════════════════════════════════════
  // TEXTO JSX MIGRADO AL CERRAR EL GATE G2
  // ═══════════════════════════════════════════════════════════════════════
  // Nodos de texto JSX (títulos, botones, cabeceras de tabla, estados vacíos) que
  // los barridos por línea no detectaban porque son multilínea. Los destapó la
  // regla `i18next/no-literal-string`.

  // ─── Conversión masiva: progreso ───
  'master_document.crossing.bulkTitle': 'Conversión masiva',
  'master_document.crossing.bulkBackgroundNote': 'Este proceso continúa en segundo plano.',
  'master_document.crossing.bulkFailed': 'La conversión falló.',
  'master_document.crossing.bulkViewResults': 'Ver resultados',
  // Frase con cláusula opcional y DOS contadores: el detalle se compone de los
  // fragmentos ya pluralizados de abajo.
  'master_document.crossing.bulkCompletedWith': 'Completado — {detail}',
  'master_document.crossing.bulkErrorCount_one': '{count} error',
  'master_document.crossing.bulkErrorCount_other': '{count} errores',
  'master_document.crossing.bulkPartialCount_one': '{count} parcial',
  'master_document.crossing.bulkPartialCount_other': '{count} parciales',
  'master_document.crossing.bulkCancelled_one':
    'Cancelado — {count} documento procesado antes de cancelar.',
  'master_document.crossing.bulkCancelled_other':
    'Cancelado — {count} documentos procesados antes de cancelar.',

  // ─── Conversión masiva: resultados ───
  'master_document.crossing.bulkResultsTitle': 'Resultados de la conversión masiva',
  'master_document.crossing.bulkSuccessCount_one': '{count} exitoso',
  'master_document.crossing.bulkSuccessCount_other': '{count} exitosos',
  'master_document.crossing.bulkWithErrorCount_one': '{count} con error',
  'master_document.crossing.bulkWithErrorCount_other': '{count} con error',
  'master_document.crossing.bulkColumn.sourceDocument': 'Documento fuente',
  'master_document.crossing.bulkColumn.result': 'Resultado',
  'master_document.crossing.bulkColumn.generatedDocument': 'Documento generado',
  'master_document.crossing.bulkStatus.success': 'Exitoso',
  'master_document.crossing.bulkBlockedLines_one': '{count} línea bloqueada',
  'master_document.crossing.bulkBlockedLines_other': '{count} líneas bloqueadas',
  'master_document.crossing.bulkSelectFailed': 'Seleccionar fallidos y reintentar:',
  'master_document.crossing.bulkRetryFailed_one': 'Reintentar {count} fallido',
  'master_document.crossing.bulkRetryFailed_other': 'Reintentar {count} fallidos',

  // ─── Cruce: generar y seleccionar orígenes ───
  'master_document.crossing.generateTitle': 'Generar {class}',
  'master_document.crossing.generateQuestion':
    '¿Generar {class} a partir de las líneas seleccionadas?',
  'master_document.crossing.generateDocument': 'Generar documento',
  'master_document.crossing.sourceDocuments': 'Documentos fuente',
  'master_document.crossing.selectSourceHint': 'Selecciona uno o más documentos fuente',
  'master_document.crossing.addSourceDocument': 'Agregar documento fuente',
  'master_document.crossing.noRelations':
    'Este documento no tiene relaciones de cruce registradas.',
  'master_document.crossing.current': 'Actual',
  // Se rediseñó para no depender del número: el estado vacío no tiene el contador
  // de orígenes en alcance, así que `documento(s)` se resolvió en plural genérico.
  'master_document.crossing.noPendingLinesAll':
    'No hay líneas pendientes. Todas las líneas de los documentos seleccionados ya fueron cumplidas o cerradas.',

  // ─── Chrome adicional ───
  'master_document.chrome.retry': 'Reintentar',
  'master_document.chrome.refresh': 'Actualizar',
  'master_document.chrome.understood': 'Entendido',
  'master_document.chrome.noData': 'Sin datos',

  // ─── Conflicto de concurrencia ───
  'master_document.document.conflictTitle': 'Alguien más editó este documento',
  'master_document.document.conflictModifiedBy': '{user} guardó cambios {when}.',
  'master_document.document.conflictRecently': 'recientemente',
  'master_document.document.conflictReloadWarning':
    'Si recargas, verás los cambios más recientes pero perderás lo que acabas de modificar.',
  'master_document.document.conflictWhatToDo': '¿Qué quieres hacer?',
  'master_document.document.conflictKeepChanges': 'Seguir con mis cambios',
  'master_document.document.conflictReload': 'Recargar documento',
  'master_document.document.printAvailableSoon': 'Estará disponible próximamente.',

  // ─── Asistente de creación ───
  'master_document.wizard.subtitle': 'Selecciona la configuración para crear el documento',
  'master_document.wizard.company': 'Compañía',
  'master_document.wizard.documentClass': 'Clase de documento',
  'master_document.wizard.operationCenter': 'Centro de operación',
  'master_document.wizard.documentType': 'Tipo de documento',
  'master_document.wizard.selectOperationCenterHint':
    'Selecciona un centro de operación para ver los tipos disponibles',

  // ─── Listado ───
  'master_document.list.advancedSearch': 'Búsqueda avanzada',
  'master_document.list.clear': 'Limpiar',
  'master_document.list.filterColumn': 'Filtrar',
  'master_document.list.filterAll': 'Todos',
  'master_document.list.filterPlaceholder': 'Filtrar...',
  'master_document.list.recordCount_one': '{count} registro',
  'master_document.list.recordCount_other': '{count} registros',
  'master_document.list.selectedCount_one': '{count} documento seleccionado',
  'master_document.list.selectedCount_other': '{count} documentos seleccionados',
  'master_document.list.previousShort': '‹ Anterior',
  'master_document.list.nextShort': 'Siguiente ›',

  // ─── Moneda ───
  'master_document.currency.changeTitle': 'Cambiar moneda',
  'master_document.currency.notFound': 'No se encontraron monedas',
  'master_document.currency.base': 'Base',

  // ─── FactBox / historial / CFDE ───
  'master_document.factBox.info': 'Datos +',
  'master_document.factBox.financial.pendingPosting': 'Pendiente de contabilización',
  'master_document.factBox.financial.localEquivalent': 'Equivalente en moneda local (COP)',
  'master_document.factBox.customWidget': 'Widget personalizado: {key}',
  'master_document.statusHistory.empty': 'Sin historial de estados',
  'master_document.statusHistory.creation': 'Creación',
  'master_document.cfde.viewQr': 'Ver QR DIAN',
  'master_document.cfde.retransmitButton': 'Retransmitir',
  'master_document.cfde.downloadPdfButton': 'Descargar PDF',

  // ─── Líneas ───
  'master_document.lines.closeReason': 'Razón de cierre',
  'master_document.lines.reopenConfirm':
    '¿Confirmas que deseas reabrir esta línea para que vuelva al flujo de cumplimiento?',
  'master_document.lines.saveAndAddAnother': 'Guardar y agregar otra',
  'master_document.lines.calculated': '(calculado)',
  'master_document.lines.tab': 'Líneas',
  'master_document.lines.tabsLabel': 'Listados de líneas',
  'master_document.lines.tabUnavailable': 'Este listado no está disponible',

  // ─── Campos adicionales (entidades dinámicas / EAV) ───
  'master_document.dynamicEntities.headerTitle': 'Campos adicionales',
  'master_document.dynamicEntities.lineTitle': 'Campos adicionales de la línea',

  // ─── Varios ───
  'master_document.discount.globalLabel': 'Descuento global',
  'master_document.staleData.note': 'Los estados de cumplimiento pueden haber cambiado.',
};

export const MASTER_DOCUMENT_TRANSLATIONS_EN: Record<string, string> = {
  // ─── Chrome de modales / diálogos ───
  'master_document.chrome.close': 'Close',
  'master_document.chrome.closeDialog': 'Close dialog',
  'master_document.chrome.closeNotice': 'Dismiss notice',
  'master_document.chrome.restoreSize': 'Restore size',
  'master_document.chrome.maximize': 'Maximize',
  'master_document.chrome.updating': 'Updating',
  'master_document.chrome.updatingTitle': 'Updating…',
  'master_document.chrome.loadingValue': 'Loading value',
  'master_document.chrome.actionRunning': 'Running action',
  'master_document.chrome.confirm': 'Confirm',
  'master_document.chrome.cancel': 'Cancel',
  'master_document.chrome.back': 'Back',
  'master_document.chrome.backToList': 'Back',
  'master_document.chrome.next': 'Next',
  'master_document.chrome.error': 'Error',

  // ─── Barra de acciones / encabezado ───
  'master_document.actionBar.aria': 'Document action bar',
  'master_document.actionBar.moreActions': 'More actions',
  'master_document.actionBar.transitions': 'Transitions',
  'master_document.actionBar.actions': 'Actions',
  'master_document.header.sectionsAria': 'Header sections',
  'master_document.contextStrip.aria': 'Document context',

  // ─── FactBox ───
  'master_document.factBox.aria': 'Information panel',

  // ─── FactBox: condiciones de pago ───
  'master_document.factBox.paymentTerms.dueDays': 'Due days',
  'master_document.factBox.paymentTerms.earlyPaymentEnabled': 'Early payment enabled',
  'master_document.factBox.paymentTerms.window': 'Window',
  'master_document.factBox.paymentTerms.advanceRequired': 'Advance required',
  'master_document.factBox.paymentTerms.advanceAmount': 'Advance amount',
  'master_document.factBox.paymentTerms.days_one': '{count} day',
  'master_document.factBox.paymentTerms.days_other': '{count} days',
  'master_document.factBox.paymentTerms.installments': 'Installments',
  'master_document.factBox.paymentTerms.mode': 'Mode',
  'master_document.factBox.paymentTerms.discount': 'Discount',
  'master_document.factBox.paymentTerms.periodDays': 'Days',
  'master_document.factBox.paymentTerms.periodMonths': 'Months',
  'master_document.factBox.paymentTerms.periodCustom': 'Custom',

  // ─── FactBox: financiero ───
  'master_document.factBox.financial.receivables': 'Accounts receivable',
  'master_document.factBox.financial.payables': 'Accounts payable',
  'master_document.factBox.financial.crossedAdvances': 'Crossed advances',
  'master_document.factBox.financial.localReceivables': 'Local AR',
  'master_document.factBox.financial.localPayables': 'Local AP',
  'master_document.factBox.financial.cash': 'Cash',

  // ─── FactBox CFDE / factura electrónica ───
  'master_document.cfde.dianStatus': 'DIAN status',
  'master_document.cfde.feNumber': 'E-invoice number',
  'master_document.cfde.issueDate': 'Issue date',
  'master_document.cfde.prints': 'Prints',
  'master_document.cfde.retransmit': 'Retransmit document to DIAN',
  'master_document.cfde.downloadPdf': 'Download e-invoice PDF',
  'master_document.cfde.status.pending': 'Pending',
  'master_document.cfde.status.sent': 'Sent',
  'master_document.cfde.status.accepted': 'Accepted',
  'master_document.cfde.status.rejected': 'Rejected',
  'master_document.cfde.status.error': 'Error',
  'master_document.cfde.statusAria': 'Status: {status}',

  // ─── Historial de estados: etiquetas de transición del backend ───
  'master_document.statusHistory.action.create': 'Create document',
  'master_document.statusHistory.action.save': 'Save',
  'master_document.statusHistory.action.approve': 'Approve',
  'master_document.statusHistory.action.reject': 'Reject',
  'master_document.statusHistory.action.void': 'Void',
  'master_document.statusHistory.action.print': 'Print',
  'master_document.statusHistory.action.sendToApproval': 'Send for approval',
  'master_document.statusHistory.action.hold': 'Hold',
  'master_document.statusHistory.action.release': 'Release',
  'master_document.statusHistory.action.commit': 'Commit',
  'master_document.statusHistory.action.dispatch': 'Dispatch',
  'master_document.statusHistory.action.post': 'Post',
  'master_document.statusHistory.action.invoice': 'Invoice',
  'master_document.statusHistory.action.fulfill': 'Fulfill',
  'master_document.statusHistory.action.partialFulfillment': 'Partial fulfillment',
  'master_document.statusHistory.action.advance': 'Advance',
  'master_document.statusHistory.action.collection': 'Collection',

  // ─── Banner de aprobación ───
  'master_document.approval.approve': 'Approve',
  'master_document.approval.reject': 'Reject',

  // ─── Badge de cumplimiento de línea ───
  'master_document.lineFulfillment.pending': 'Pending',
  'master_document.lineFulfillment.partial': 'Partial',
  'master_document.lineFulfillment.fulfilled': 'Fulfilled',
  'master_document.lineFulfillment.closed': 'Closed',

  // ─── Listado de documentos ───
  'master_document.list.rowActions': 'Actions',
  'master_document.list.rowOptions': 'Options',
  'master_document.list.selectAll': 'Select all',
  'master_document.list.selectRow': 'Select row',
  'master_document.list.previousPage': 'Previous page',
  'master_document.list.nextPage': 'Next page',
  'master_document.list.reload': 'Reload',
  'master_document.list.selectCompany': 'Select company',
  'master_document.list.noneSelected': 'No document selected',
  'master_document.list.noRecords': 'No records',
  'master_document.list.loadError': 'Error loading data',
  'master_document.list.new': 'New',
  // List toolbar and paginator, aligned with the MasterCrud canon.
  'master_document.list.moreActions': 'More actions',
  'master_document.list.viewDetail': 'View detail',
  'master_document.list.deactivateRecord': 'Deactivate record',
  'master_document.list.activateRecord': 'Activate record',
  'master_document.list.moreOptions': 'More options',
  'master_document.list.hideAdvancedSearch': 'Hide advanced search',
  'master_document.list.perPage': 'per page',
  'master_document.list.records': 'records',
  'master_document.list.goToPage': 'Page',
  'master_document.list.noResults': 'No documents found',
  'master_document.list.export': 'Export',
  'master_document.list.print': 'Print',
  'master_document.list.confirmAction': 'Do you want to run this action?',
  'master_document.list.allCompanies': 'Global — All companies',
  'master_document.list.action.delete': 'Delete',
  'master_document.list.action.void': 'Void',

  // ─── Sección de líneas ───
  'master_document.lines.selectLine': 'Select line',
  'master_document.lines.selectAllLines': 'Select all lines',
  'master_document.lines.editLine': 'Edit line',
  'master_document.lines.view': 'View',
  'master_document.lines.viewLine': 'View line',
  'master_document.lines.moreOptions': 'More options',
  'master_document.lines.moreLineOptions': 'More line options',
  'master_document.lines.closeLine': 'Close line',
  'master_document.lines.reopenLine': 'Reopen line',
  'master_document.lines.fieldRequired': 'This field is required.',
  'master_document.lines.emptyTitle': 'No detail lines',
  'master_document.lines.emptyHint': 'Add a new line to get started',
  'master_document.lines.closeReasonPlaceholder': 'Describe the reason for closing...',
  'master_document.lines.title': 'Document lines',
  'master_document.lines.newLine': 'New line',
  'master_document.lines.addLine': 'Add line',
  'master_document.lines.saveChanges': 'Save changes',
  'master_document.lines.edit': 'Edit',
  'master_document.lines.duplicate': 'Duplicate',
  'master_document.lines.delete': 'Delete',
  'master_document.lines.deleteConfirm': 'Delete this line? This action cannot be undone.',

  // ─── Documento ───
  'master_document.document.deleteConfirm': 'Delete this document? This action cannot be undone.',
  'master_document.document.traceability': 'Traceability',
  'master_document.document.auditTitle': 'Change history',
  'master_document.document.print': 'Print',
  'master_document.document.printNotImplemented':
    'Printing is not implemented yet for this document.',
  'master_document.document.conflictBody':
    'Another session modified this document. Your changes were not saved. Reload to see the current version and reapply them.',

  // ─── Campos / búsqueda ───
  'master_document.field.search': 'Search',
  'master_document.field.searchPlaceholder': 'Search...',
  'master_document.field.selectPlaceholder': 'Select...',
  'master_document.field.required': 'This field is required',
  'master_document.field.lockTooltip': 'Remove the lines to be able to change this field.',

  // ─── Asistente de creación ───
  'master_document.wizard.aria': 'New document',
  'master_document.wizard.selectCompany': 'Select company...',
  'master_document.wizard.selectDocumentClass': 'Select document class...',
  'master_document.wizard.selectOperationCenter': 'Select operation center...',

  // ─── Moneda ───
  'master_document.currency.searchPlaceholder': 'Search currency...',
  'master_document.currency.listAria': 'Available currencies',

  // ─── Descuento global ───
  'master_document.discount.applyPercent': 'Apply percentage discount',
  'master_document.discount.applyAmount': 'Apply fixed amount discount',
  'master_document.discount.valueAria': 'Discount value',

  // ─── Cruce de documentos ───
  'master_document.crossing.searchPlaceholder': 'Search by document number...',
  'master_document.crossing.addPlaceholder': 'Document no. and Enter to add...',
  'master_document.crossing.selectRow': 'Select',
  'master_document.crossing.bulkProgress': 'Processing documents...',
  'master_document.crossing.bulkCompleted': 'Completed successfully',
  'master_document.crossing.traceabilityLoading': 'Loading traceability tree',
  'master_document.crossing.pending.item': 'Item',
  'master_document.crossing.pending.document': 'Document',
  'master_document.crossing.pending.pending': 'Pending',
  'master_document.crossing.pending.state': 'State',
  'master_document.crossing.pending.quantityToCross': 'Quantity to cross',
  'master_document.crossing.selectLineToGenerate': 'Select at least one line to generate the document.',
  'master_document.crossing.noGeneratedDocument': 'The backend did not return the generated document.',
  'master_document.crossing.pending.selectLineAria': 'Select line {item}',
  'master_document.crossing.pending.selectDocumentAria': 'Select {document}',
  'master_document.crossing.pending.quantityAria': 'Quantity to cross for {item}',

  // ─── Panel de cruce: textos con canal de override por template ───
  'master_document.crossing.panelTitle': 'Cross source documents',
  'master_document.crossing.emptyAvailable': 'No source documents available to cross.',
  'master_document.crossing.emptyCrossed': 'No crossed source documents.',
  'master_document.crossing.subtotalLabel': 'Crossing subtotal',
  'master_document.crossing.crossButton': 'Cross selected',
  'master_document.crossing.uncross': 'Unlink',
  'master_document.crossing.loading': 'Loading…',
  'master_document.crossing.crossing': 'Crossing…',
  'master_document.crossing.blockedLinesCount_one': '{count} line not crossed',
  'master_document.crossing.blockedLinesCount_other': '{count} lines not crossed',
  'master_document.crossing.lineCount_one': '{count} line',
  'master_document.crossing.lineCount_other': '{count} lines',
  'master_document.crossing.terminalLineMessage': 'A closed or fulfilled line cannot be selected',
  'master_document.crossing.quantityRequired': 'Enter a quantity to cross.',
  'master_document.crossing.quantityPositive': 'The quantity must be greater than zero.',
  'master_document.crossing.quantityExceedsPending':
    'The quantity cannot exceed the pending amount ({pending}{uom}).',

  // ─── Asistente "crear desde origen" (canal `config.i18n`) ───
  'master_document.createFromSource.originSelectorTitle': 'New document',
  'master_document.createFromSource.step1Title': 'Select source documents',
  'master_document.createFromSource.step2Title': 'Select lines',
  'master_document.createFromSource.fromSourceOption': 'Generate from source',
  'master_document.createFromSource.freeCaptureOption': 'Direct capture (no source)',
  'master_document.createFromSource.loadingSources': 'Loading source documents…',
  'master_document.createFromSource.emptyState': 'No source documents available.',
  'master_document.createFromSource.singleSelectHint':
    'Only one source document can be selected.',
  'master_document.createFromSource.loadingLines': 'Loading pending lines…',
  'master_document.createFromSource.emptyLines_one': 'No pending lines in the selected document.',
  'master_document.createFromSource.emptyLines_other':
    'No pending lines in the selected documents.',
  'master_document.createFromSource.quantityColumn': 'Quantity to cross',
  'master_document.createFromSource.confirmSummary':
    '{lines} {linesLabel} will be created from {sources} {sourcesLabel}.',
  'master_document.createFromSource.lineNoun_one': 'line',
  'master_document.createFromSource.lineNoun_other': 'lines',
  'master_document.createFromSource.sourceNoun_one': 'source document',
  'master_document.createFromSource.sourceNoun_other': 'source documents',
  'master_document.createFromSource.incompatibleReason':
    'Incompatible with the first source ({keys}).',
  'master_document.createFromSource.columns.document': 'Document',
  'master_document.createFromSource.columns.thirdParty': 'Third party',
  'master_document.createFromSource.columns.date': 'Date',
  'master_document.createFromSource.columns.state': 'State',
  'master_document.createFromSource.columns.total': 'Total',

  // ─── Estados de carga ───
  'master_document.loading.document': 'Loading document...',
  'master_document.loading.list': 'Loading list...',
  'master_document.staleData.refresh': 'Refresh data',

  // ─── Errores genéricos ───
  'master_document.error.actionFailed': 'The action could not be completed.',
  'master_document.error.unexpected': 'An unexpected error occurred. Please try again.',
  'master_document.error.autoSaveTotals': 'Totals auto-save: {detail}',
  'master_document.error.unauthorized': 'Your session expired. Please sign in again.',
  'master_document.error.forbidden': 'You do not have permission for this operation.',
  'master_document.error.notFound': 'The document does not exist or was deleted.',
  'master_document.error.conflict':
    'The document changed since you opened it. Reload to see the current version.',
  'master_document.error.validation': 'Check the entered data.',
  'master_document.error.serverUnavailable':
    'The service is unavailable. Please try again in a few minutes.',
  'master_document.error.network':
    'No connection to the server. Check your network and try again.',

  // ═══════════════════════════════════════════════════════════════════════
  // TEXTO JSX MIGRADO AL CERRAR EL GATE G2
  // ═══════════════════════════════════════════════════════════════════════

  // ─── Conversión masiva: progreso ───
  'master_document.crossing.bulkTitle': 'Bulk conversion',
  'master_document.crossing.bulkBackgroundNote': 'This process continues in the background.',
  'master_document.crossing.bulkFailed': 'The conversion failed.',
  'master_document.crossing.bulkViewResults': 'View results',
  'master_document.crossing.bulkCompletedWith': 'Completed — {detail}',
  'master_document.crossing.bulkErrorCount_one': '{count} error',
  'master_document.crossing.bulkErrorCount_other': '{count} errors',
  'master_document.crossing.bulkPartialCount_one': '{count} partial',
  'master_document.crossing.bulkPartialCount_other': '{count} partial',
  'master_document.crossing.bulkCancelled_one':
    'Cancelled — {count} document processed before cancelling.',
  'master_document.crossing.bulkCancelled_other':
    'Cancelled — {count} documents processed before cancelling.',

  // ─── Conversión masiva: resultados ───
  'master_document.crossing.bulkResultsTitle': 'Bulk conversion results',
  'master_document.crossing.bulkSuccessCount_one': '{count} successful',
  'master_document.crossing.bulkSuccessCount_other': '{count} successful',
  'master_document.crossing.bulkWithErrorCount_one': '{count} with an error',
  'master_document.crossing.bulkWithErrorCount_other': '{count} with errors',
  'master_document.crossing.bulkColumn.sourceDocument': 'Source document',
  'master_document.crossing.bulkColumn.result': 'Result',
  'master_document.crossing.bulkColumn.generatedDocument': 'Generated document',
  'master_document.crossing.bulkStatus.success': 'Successful',
  'master_document.crossing.bulkBlockedLines_one': '{count} blocked line',
  'master_document.crossing.bulkBlockedLines_other': '{count} blocked lines',
  'master_document.crossing.bulkSelectFailed': 'Select failed and retry:',
  'master_document.crossing.bulkRetryFailed_one': 'Retry {count} failed',
  'master_document.crossing.bulkRetryFailed_other': 'Retry {count} failed',

  // ─── Cruce: generar y seleccionar orígenes ───
  'master_document.crossing.generateTitle': 'Generate {class}',
  'master_document.crossing.generateQuestion': 'Generate {class} from the selected lines?',
  'master_document.crossing.generateDocument': 'Generate document',
  'master_document.crossing.sourceDocuments': 'Source documents',
  'master_document.crossing.selectSourceHint': 'Select one or more source documents',
  'master_document.crossing.addSourceDocument': 'Add source document',
  'master_document.crossing.noRelations': 'This document has no recorded crossing relations.',
  'master_document.crossing.current': 'Current',
  'master_document.crossing.noPendingLinesAll':
    'No pending lines. Every line in the selected documents has been fulfilled or closed.',

  // ─── Chrome adicional ───
  'master_document.chrome.retry': 'Retry',
  'master_document.chrome.refresh': 'Refresh',
  'master_document.chrome.understood': 'Understood',
  'master_document.chrome.noData': 'No data',

  // ─── Conflicto de concurrencia ───
  'master_document.document.conflictTitle': 'Someone else edited this document',
  'master_document.document.conflictModifiedBy': '{user} saved changes {when}.',
  'master_document.document.conflictRecently': 'recently',
  'master_document.document.conflictReloadWarning':
    'If you reload you will see the latest changes but lose what you just modified.',
  'master_document.document.conflictWhatToDo': 'What do you want to do?',
  'master_document.document.conflictKeepChanges': 'Keep my changes',
  'master_document.document.conflictReload': 'Reload document',
  'master_document.document.printAvailableSoon': 'It will be available soon.',

  // ─── Asistente de creación ───
  'master_document.wizard.subtitle': 'Select the configuration to create the document',
  'master_document.wizard.company': 'Company',
  'master_document.wizard.documentClass': 'Document class',
  'master_document.wizard.operationCenter': 'Operation center',
  'master_document.wizard.documentType': 'Document type',
  'master_document.wizard.selectOperationCenterHint':
    'Select an operation center to see the available types',

  // ─── Listado ───
  'master_document.list.advancedSearch': 'Advanced search',
  'master_document.list.filterColumn': 'Filter',
  'master_document.list.filterAll': 'All',
  'master_document.list.filterPlaceholder': 'Filter...',
  'master_document.list.clear': 'Clear',
  'master_document.list.recordCount_one': '{count} record',
  'master_document.list.recordCount_other': '{count} records',
  'master_document.list.selectedCount_one': '{count} document selected',
  'master_document.list.selectedCount_other': '{count} documents selected',
  'master_document.list.previousShort': '‹ Previous',
  'master_document.list.nextShort': 'Next ›',

  // ─── Moneda ───
  'master_document.currency.changeTitle': 'Change currency',
  'master_document.currency.notFound': 'No currencies found',
  'master_document.currency.base': 'Base',

  // ─── FactBox / historial / CFDE ───
  'master_document.factBox.info': 'Data +',
  'master_document.factBox.financial.pendingPosting': 'Pending posting',
  'master_document.factBox.financial.localEquivalent': 'Local currency equivalent (COP)',
  'master_document.factBox.customWidget': 'Custom widget: {key}',
  'master_document.statusHistory.empty': 'No status history',
  'master_document.statusHistory.creation': 'Creation',
  'master_document.cfde.viewQr': 'View DIAN QR',
  'master_document.cfde.retransmitButton': 'Retransmit',
  'master_document.cfde.downloadPdfButton': 'Download PDF',

  // ─── Líneas ───
  'master_document.lines.closeReason': 'Reason for closing',
  'master_document.lines.reopenConfirm':
    'Do you confirm reopening this line so it returns to the fulfillment flow?',
  'master_document.lines.saveAndAddAnother': 'Save and add another',
  'master_document.lines.calculated': '(calculated)',
  'master_document.lines.tab': 'Lines',
  'master_document.lines.tabsLabel': 'Line lists',
  'master_document.lines.tabUnavailable': 'This list is not available',

  // ─── Campos adicionales (entidades dinámicas / EAV) ───
  'master_document.dynamicEntities.headerTitle': 'Additional fields',
  'master_document.dynamicEntities.lineTitle': 'Line additional fields',

  // ─── Varios ───
  'master_document.discount.globalLabel': 'Global discount',
  'master_document.staleData.note': 'Fulfillment statuses may have changed.',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const MASTER_DOCUMENT_TRANSLATIONS = {
  es: MASTER_DOCUMENT_TRANSLATIONS_ES,
  en: MASTER_DOCUMENT_TRANSLATIONS_EN,
} as const;

/** Función de traducción del core con interpolación `{var}`. */
export type MasterDocumentT = TranslateFn;

/**
 * Hook de traducción del core de MasterDocument.
 *
 * @example
 * ```tsx
 * const { t } = useMasterDocumentTranslation();
 * <button aria-label={t('master_document.chrome.close')} />
 * ```
 */
export const useMasterDocumentTranslation = createTranslationHook(MASTER_DOCUMENT_TRANSLATIONS);
